import {
  Avatar,
  Box,
  CardContent,
  Card,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import type { User } from '@verantos-dev/core-annotation-tool';

import { getInitials } from './getInitials';
import { PeopleSectionEditable } from './PeopleSectionEditable';

interface PeopleSectionProps {
  annotatorNames: string[];
  supervisorNames: string[];
  /** Accepted for caller compatibility. This component renders its content immediately and does not use it. */
  isLoading?: boolean;
  /** When true, renders the editable variant (add/remove people). */
  editable?: boolean;
  persistedAnnotators?: User[];
  persistedSupervisors?: User[];
  addedAnnotators?: User[];
  addedSupervisors?: User[];
  onAddAnnotator?: (person: User) => void;
  onRemoveAnnotator?: (id: string) => void;
  onAddSupervisor?: (person: User) => void;
  onRemoveSupervisor?: (id: string) => void;
}

export const AnnotatorInitialTags = ({ names }: { names: string[] }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1}>
      {names.map((name) => (
        <Tooltip key={name} title={name} arrow>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: 14,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            {getInitials(name)}
          </Avatar>
        </Tooltip>
      ))}
    </Stack>
  );
};

const PeopleSectionCard = ({ title, names }: { title: string; names: string[] }) => {
  const theme = useTheme();
  return (
    <Card sx={{ flex: 1 }} elevation={2}>
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          gap: theme.spacing(3),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography variant="h6">{title}</Typography>
          <Typography variant="h6">{names.length}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <AnnotatorInitialTags names={names} />
        </Box>
      </CardContent>
    </Card>
  );
};

export const PeopleSection = ({
  annotatorNames,
  supervisorNames,
  editable = false,
  persistedAnnotators = [],
  persistedSupervisors = [],
  addedAnnotators = [],
  addedSupervisors = [],
  onAddAnnotator,
  onRemoveAnnotator,
  onAddSupervisor,
  onRemoveSupervisor,
}: PeopleSectionProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  if (editable) {
    return (
      <PeopleSectionEditable
        persistedAnnotators={persistedAnnotators}
        persistedSupervisors={persistedSupervisors}
        addedAnnotators={addedAnnotators}
        addedSupervisors={addedSupervisors}
        onAddAnnotator={onAddAnnotator ?? (() => undefined)}
        onRemoveAnnotator={onRemoveAnnotator ?? (() => undefined)}
        onAddSupervisor={onAddSupervisor ?? (() => undefined)}
        onRemoveSupervisor={onRemoveSupervisor ?? (() => undefined)}
      />
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <Typography variant="h6">
        {formatMessage({ id: 'admin-common.peopleSection.title' })}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: theme.spacing(6),
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <PeopleSectionCard
          title={formatMessage({ id: 'admin-common.peopleSection.annotators' })}
          names={annotatorNames}
        />
        <PeopleSectionCard
          title={formatMessage({ id: 'admin-common.peopleSection.supervisors' })}
          names={supervisorNames}
        />
      </Box>
    </Box>
  );
};
